import type { MotionProps } from 'motion/react'
import { motion } from 'motion/react'
import { useEffect, useState } from 'react'
import { useInView } from 'react-intersection-observer'

interface AnimateInViewProps extends MotionProps {
  children: React.ReactNode
  threshold?: number
  animateOnce?: boolean
  className?: string
  /** The delay in seconds before the animation starts. */
  delay?: number
  /** Preassign objects to initial, animate, and transition props. Defaults to 'fade-in'. */
  presets?: 'fade-in' | 'slide-right' | 'slide-left' | 'slide-down' | 'slide-up'
}

const ANIMATION_PRESETS = {
  'fade-in': {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    transition: { duration: 0.5 },
  },
  'slide-right': {
    initial: { opacity: 0, x: -20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5 },
  },
  'slide-left': {
    initial: { opacity: 0, x: 20 },
    animate: { opacity: 1, x: 0 },
    transition: { duration: 0.5 },
  },
  'slide-down': {
    initial: { opacity: 0, y: -20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  },
  'slide-up': {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
  },
}

/**
 * A component wrapper that is used to animate components when they come into view.
 * By default, the component will do a fade in animation.
 * Use the initial and animate props for more control over the animation. Refer to the motion library for api.
 */
export default function AnimateInView({
  children,
  threshold = 0.2,
  animateOnce = true,
  className,
  initial,
  animate,
  transition,
  delay = 0.1,
  presets = 'fade-in',
  ...props
}: AnimateInViewProps) {
  const [forceShowAnimation, setForceShowAnimation] = useState(false)

  const { ref, inView } = useInView({
    threshold,
  })

  useEffect(() => {
    if (inView && animateOnce) {
      setForceShowAnimation(true)
    }
  }, [inView, animateOnce])

  // Force show animation prevents the component from hiding when it's not in view.
  const showAnimation = inView || forceShowAnimation

  const initialObj = initial || ANIMATION_PRESETS[presets].initial
  const animateObj = animate || ANIMATION_PRESETS[presets].animate
  const transitionObj = transition || ANIMATION_PRESETS[presets].transition

  return (
    <motion.div
      ref={ref}
      initial={initialObj}
      animate={showAnimation ? animateObj : initialObj}
      transition={{ ...transitionObj, delay }}
      className={className}
      {...props}
    >
      {children}
    </motion.div>
  )
}
